import React from 'react';
import { AppUser } from '../../../types';
import { XMarkIcon, SpinnerIcon, IdentificationIcon } from '../../Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../../../constants';
import { EditProfileFormFields } from './EditProfileFormFields';
import { useEditProfileForm } from './useEditProfileForm';
import { useBodyScrollLock } from './useBodyScrollLock';
import { ProfileConfirmSaveModal } from './ProfileConfirmSaveModal';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  appUser: AppUser;
  onOpenCarnet?: () => void;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
  appUser,
  onOpenCarnet,
}) => {
  useBodyScrollLock(isOpen);

  const {
    formData,
    loading,
    error,
    success,
    isConfirmOpen,
    setIsConfirmOpen,
    editableFields,
    isAnyFieldEditing,
    handleFieldChange,
    handleToggleEditField,
    handleCloseEditField,
    handleToggleCity,
    handleToggleZone,
    handleSelectAllZones,
    handleClearZones,
    handleRequestSave,
    handleConfirmSave,
  } = useEditProfileForm(appUser, isOpen, onClose);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-[200] flex items-center justify-center p-2 sm:p-4 overflow-hidden select-none">
        <div className="bg-white rounded-2xl sm:rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden animate-fade-in-up border border-gray-100">
          <header className="px-5 sm:px-7 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/70 flex-shrink-0">
            <div>
              <h2 className="text-base sm:text-lg font-black text-gray-800 uppercase tracking-tight">
                Mi Perfil
              </h2>
              <p className="text-[11px] sm:text-xs text-sky-600 font-bold">
                Datos personales y zonas de cobertura
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
              aria-label="Cerrar"
            >
              <XMarkIcon className="w-5 h-5 text-gray-400" />
            </button>
          </header>

          <form onSubmit={handleRequestSave} className="flex flex-col flex-1 overflow-hidden min-h-0 select-text">
            <div className="p-5 sm:p-7 space-y-5 overflow-y-auto flex-1 overscroll-contain [scrollbar-width:thin] [scrollbar-color:#94a3b8_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
              {error && (
                <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-semibold">
                  {error}
                </div>
              )}
              {success && (
                <div className="p-3.5 bg-green-50 border border-green-200 rounded-xl text-green-700 text-xs font-semibold">
                  ¡Perfil guardado con éxito!
                </div>
              )}

              <EditProfileFormFields
                formData={formData}
                email={appUser.email}
                onChange={handleFieldChange}
                editableFields={editableFields}
                onToggleEditField={handleToggleEditField}
                onCloseEditField={handleCloseEditField}
                onToggleCity={handleToggleCity}
                onToggleZone={handleToggleZone}
                onSelectAllZones={handleSelectAllZones}
                onClearZones={handleClearZones}
              />

              {onOpenCarnet && (
                <div className="pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onOpenCarnet();
                    }}
                    className="w-full py-3 px-4 bg-sky-50 border-2 border-sky-200 hover:bg-sky-100 text-sky-800 rounded-xl font-extrabold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-xs cursor-pointer"
                  >
                    <IdentificationIcon className="w-5 h-5 text-sky-600" />
                    <span>Ver Mi Carnet Virtual</span>
                  </button>
                </div>
              )}
            </div>

            <footer className="px-5 sm:px-7 py-3.5 bg-gray-50/70 border-t border-gray-100 flex gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 text-xs font-black uppercase text-gray-400 hover:text-gray-700 tracking-widest cursor-pointer"
              >
                Cerrar
              </button>
              {isAnyFieldEditing && (
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 text-white rounded-xl font-black uppercase text-xs tracking-widest shadow-md hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                >
                  {loading ? <SpinnerIcon className="w-4 h-4 animate-spin" /> : 'Guardar Perfil'}
                </button>
              )}
            </footer>
          </form>
        </div>
      </div>

      <ProfileConfirmSaveModal
        isOpen={isConfirmOpen}
        onConfirm={handleConfirmSave}
        onCancel={() => setIsConfirmOpen(false)}
        loading={loading}
      />
    </>
  );
};
